import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowRight, Search, Tag } from 'lucide-react';
import { articlesService } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

function BlogPage() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadData();
  }, [selectedCategory]);

  const loadData = async () => {
    try {
      const [articlesRes, categoriesRes] = await Promise.all([
        articlesService.getAll({ category: selectedCategory || undefined }),
        articlesService.getCategories()
      ]);
      
      setArticles(articlesRes.data.articles || defaultArticles);
      setCategories(categoriesRes.data.categories || []);
    } catch (error) {
      console.error('Erreur:', error);
      setArticles(defaultArticles);
    } finally {
      setLoading(false);
    }
  };

  const filteredArticles = articles.filter(article =>
    article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.excerpt?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (date) => {
    try {
      return format(new Date(date), 'd MMMM yyyy', { locale: fr });
    } catch {
      return '';
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-dark-900 via-primary-900 to-dark-900 overflow-hidden">
        <div className="absolute inset-0 hero-pattern opacity-10" />
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur rounded-full text-primary-200 mb-6">
              Blog & Actualités
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Insights & Tendances{' '}
              <span className="text-primary-400">WealthTech</span>
            </h1>
            <p className="text-xl text-dark-300">
              Découvrez nos articles sur les dernières tendances en matière de 
              transformation digitale financière et ISO 20022.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-white border-b border-dark-100 sticky top-16 z-20">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
            {/* Search */}
            <div className="relative w-full md:w-96">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" />
              <input
                type="text"
                placeholder="Rechercher un article..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input pl-12"
              />
            </div>

            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory('')}
                className={`badge ${!selectedCategory ? 'badge-primary' : 'bg-dark-100 text-dark-600 hover:bg-dark-200'} cursor-pointer transition-colors`}
              >
                Tous
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.category}
                  onClick={() => setSelectedCategory(cat.category)}
                  className={`badge ${selectedCategory === cat.category ? 'badge-primary' : 'bg-dark-100 text-dark-600 hover:bg-dark-200'} cursor-pointer transition-colors`}
                >
                  {cat.category} ({cat.count})
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="section bg-dark-50">
        <div className="container-custom">
          {loading ? (
            <div className="flex justify-center py-20">
              <LoadingSpinner size="lg" text="Chargement des articles..." />
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-dark-500 text-lg">Aucun article trouvé</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article, index) => (
                <motion.article
                  key={article.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link 
                    to={`/blog/${article.slug}`}
                    className="card-hover block h-full group overflow-hidden"
                  >
                    {/* Image */}
                    <div className="aspect-video bg-gradient-to-br from-primary-100 to-secondary-100 relative overflow-hidden">
                      {article.image_url ? (
                        <img 
                          src={article.image_url} 
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-4xl font-bold text-primary-300">W</span>
                        </div>
                      )}
                      {article.category && (
                        <span className="absolute top-4 left-4 badge-primary">
                          {article.category}
                        </span>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="p-6">
                      <div className="flex items-center gap-4 text-sm text-dark-500 mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(article.published_at || article.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          5 min
                        </span>
                      </div>
                      
                      <h2 className="text-xl font-semibold text-dark-900 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h2>
                      
                      <p className="text-dark-600 mb-4 line-clamp-3">
                        {article.excerpt}
                      </p>
                      
                      <span className="inline-flex items-center text-primary-600 font-medium">
                        Lire l'article
                        <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-20 bg-primary-600">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto text-center"
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Restez informé
            </h2>
            <p className="text-primary-100 mb-8">
              Inscrivez-vous à notre newsletter pour recevoir les dernières actualités 
              et tendances WealthTech.
            </p>
            <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Votre email"
                className="input flex-1"
              />
              <button type="submit" className="btn bg-white text-primary-600 hover:bg-primary-50">
                S'inscrire
              </button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

const defaultArticles = [
  {
    id: 1,
    title: 'Migration ISO 20022 : Guide complet pour les banques africaines',
    slug: 'migration-iso-20022-guide',
    excerpt: 'Découvrez les étapes clés pour réussir votre migration vers le standard ISO 20022.',
    category: 'ISO 20022',
    published_at: '2024-01-15'
  },
  {
    id: 2,
    title: 'WealthTech : Les tendances 2025 en Afrique',
    slug: 'wealthtech-tendances-2025',
    excerpt: 'Les innovations qui transforment la gestion de patrimoine sur le continent.',
    category: 'WealthTech',
    published_at: '2024-01-10'
  },
  {
    id: 3,
    title: 'Conformité BCEAO : Ce que vous devez savoir',
    slug: 'conformite-bceao-guide',
    excerpt: 'Les exigences réglementaires de la BCEAO et comment s\'y conformer.',
    category: 'Conformité',
    published_at: '2024-01-05'
  }
];

export default BlogPage;
